import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { Box } from '@mui/system';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import { ProfileGroup } from 'src/modules/member/models/profileGroup';
import { GridCellParams, GridColDef, GridSortModel } from '@mui/x-data-grid';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getAllProfileGroupsWithFilter } from 'src/shared/services/profileGroupService';
import { PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { Tooltip } from '@mui/material';
import { Lock } from '@mui/icons-material';
import { MemberProfileGroupSearchFilter } from '../models/memberProfileGroupSearchFilter';

type MemberProfileGroupDataGridProps = {
  loadingCallback: (loading: boolean) => void;
  searchFilter: MemberProfileGroupSearchFilter;
  searchFilterCallback: (filter: MemberProfileGroupSearchFilter) => void;
};

export const MemberProfileGroupDataGrid = (props: MemberProfileGroupDataGridProps) => {
  const { loadingCallback, searchFilter, searchFilterCallback } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const navigate = useNavigate();

  const [rows, setRows] = useState<ProfileGroup[]>([]);
  const [data, setData] = useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: searchFilter.pageSize,
    page: searchFilter.pageSize,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: searchFilter.sortCol, sort: searchFilter.sortDir }]);

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    setData((prev) => ({ ...prev, ...{ page: searchFilter.pageIndex, pageSize: searchFilter.pageSize } }));
  }, [searchFilter]);

  const onPageChange = (newPageIndex: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: newPageIndex });
  };

  const onPageSizeChange = (newPageSize: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: 0, pageSize: newPageSize });
  };

  const fetchData = useCallback(async () => {
    if (isMounted()) {
      updateData('loading', true);
      loadingCallback(true);
    }

    try {
      const result = await getAllProfileGroupsWithFilter(searchFilter);
      if (isMounted()) {
        updateData('totalRows', result.data.totalItems);

        setTimeout(() => {
          setRows(
            result.data.results.map((item) => {
              return { ...item, id: item.memberProfile_id };
            }),
          );
        }, 100);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        updateData('loading', false);
        loadingCallback(false);
      }
    }
  }, [isMounted, loadingCallback, searchFilter]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [searchFilter, fetchData, isMounted]);

  const onRowClick = (params) => {
    navigate(`${RoutesEnum.MemberProfileDetail}/${params.row.id}`);
  };

  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: t('memberProfile:profileGroupPage.profileGroup'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.title}</>;
      },
    },
    {
      field: 'profileItemsCount',
      headerName: t('memberProfile:profileGroupPage.profileItemsCount'),
      sortable: false,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.profileItemsCount}</>;
      },
    },
    {
      field: 'isEditable',
      headerName: t(''),
      align: 'right',
      headerAlign: 'right',
      sortable: false,
      flex: 2,
      renderCell: (params: GridCellParams) => {
        return (
          <Tooltip placement='left' arrow title={params.row.isEditable === true ? '' : t('memberProfile:profileGroupPage.lockedForEditing')}>
            {params.row.isEditable === true ? <></> : <Lock sx={{ color: 'text.secondary' }} />}
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        '& .MuiDataGrid-root--densityCompact .MuiDataGrid-cell': {
          py: 1,
        },
      }}
    >
      <DataGrid<ProfileGroup>
        columns={columns}
        rows={rows}
        autoHeight
        columnVisibilityModel={{
          fundraiser_id: false,
        }}
        dataModel={data}
        checkboxSelection={false}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => {
          ConsoleHelper.log('newSortModel', newSortModel);
          const sort = sortModel[0].sort === 'asc' ? 'desc' : 'asc';
          if (newSortModel.length) {
            setSortModel(newSortModel);
          } else {
            setSortModel([{ field: sortModel[0].field, sort }]);
          }
          searchFilterCallback({
            ...searchFilter,
            pageIndex: 0,
            sortCol: newSortModel.length ? newSortModel[0].field : sortModel[0].field,
            sortDir: newSortModel.length ? newSortModel[0].sort : sort,
          });
        }}
        onRowClick={onRowClick}
        selectionModel={[]}
        onSelectionModelChange={() => ConsoleHelper.log('')}
      />
    </Box>
  );
};
